import {
  Response,
  BaseTransaction,
  TransactionAML,
  TransactionWarning,
  TransactionWarningActionBody,
  apiAbortController,
  IntegrationLog,
} from '@ui/src';
import {
  CheckTransaction,
  CheckTransactionResult,
} from '../../../types/check-transaction';
import api from '../../../api/api';
import {
  BicSearch,
  BicValidation,
  CreateOutgoingTransaction,
  IbanValidation,
  OutgoingTransactionValidator,
  UpdateOutgoingTransaction,
} from '../types';
import { StatusUpdate, WarningsInfo } from '../../transactions/type';

export const outgoingTransactionsListApi = api.injectEndpoints({
  endpoints: (build) => ({
    getOutgoingList: build.query<Response<BaseTransaction>, string>({
      query: (query) => ({
        url: `/v1/transactions-outgoing${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getOutgoingListStatuses: build.query<Response<StatusUpdate>, string>({
      query: (query) => ({
        url: `/v1/transactions-outgoing/statuses${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getOutgoingTransactionById: build.query<{ data: BaseTransaction }, string>({
      query: (id) => ({
        url: `/v1/transactions-outgoing/${id}?include=beneficiary&include=contract&include=psp-integration-account`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getOutgoingTransactionValidators: build.query<
      { data: OutgoingTransactionValidator },
      void
    >({
      query: () => ({
        url: '/v1/transactions-outgoing/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateOutgoingTransaction: build.mutation<
      { data: BaseTransaction },
      { body: UpdateOutgoingTransaction; id: string }
    >({
      query: (data) => ({
        url: `/v1/transactions-outgoing/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createOutgoingTransaction: build.mutation<
      { data: BaseTransaction },
      CreateOutgoingTransaction
    >({
      query: (body) => ({
        url: `/v1/transactions-outgoing`,
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getOutgoingTransactionAmlById: build.query<
      Response<TransactionAML>,
      string
    >({
      query: (id) => ({
        url: `/v1/scores-results?transactionId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    checkOutgoingTransaction: build.mutation<
      { data: CheckTransactionResult },
      {
        body: CheckTransaction;
        abortController: {
          abortPendingRequest: () => void;
          getSignalToken: () => AbortSignal;
        };
      }
    >({
      query: ({ body, abortController }) => ({
        url: `/v1/transactions-outgoing/check`,
        method: 'POST',
        body,
        signal: abortController.getSignalToken(),
      }),
    }),
    getOutgoingTransactionWarnings: build.query<
      Response<TransactionWarning>,
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-outgoing/${id}/warnings?take=100`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getOutgoingTransactionWarning: build.query<
      { data: TransactionWarning },
      { id: string; warningId: string }
    >({
      query: (data) => ({
        url: `/v1/transactions-outgoing/${data.id}/warnings/${data.warningId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    applyOutgoingTransactionWarningAction: build.mutation<
      { data: TransactionWarning },
      {
        body: TransactionWarningActionBody;
        transactionId: string;
        warningId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/transactions-outgoing/${data.transactionId}/warnings/${data.warningId}/action`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    exportOutgoingTransaction: build.mutation<
      any,
      Record<string, string[] | string | number>
    >({
      query: (body) => ({
        url: '/v1/transactions-outgoing/export',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    resetOutgoingTransactionWarningAction: build.mutation<
      { data: TransactionWarning },
      {
        transactionId: string;
        warningId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/transactions-outgoing/${data.transactionId}/warnings/${data.warningId}/reset`,
        method: 'PUT',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getOutgoingTransactionWarningsInfo: build.query<
      { data: WarningsInfo },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-outgoing/${id}/warnings/info`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    ibanValidation: build.query<{ data: IbanValidation }, string>({
      query: (iban) => ({
        url: `/v1/iban/validate?iban=${iban}&disableCache=false&sci=1`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    bicValidation: build.query<{ data: BicValidation }, string>({
      query: (bic) => ({
        url: `/v1/iban/bic/validate?bic=${bic}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    bicSearch: build.query<
      { data: { items: BicSearch[] } },
      { bankName: string; bankCountry?: string }
    >({
      query: (data) => ({
        url: `/v1/iban/bic/search?bankName=${data.bankName}${
          data.bankCountry ? `&country=${data.bankCountry}` : ''
        }&limit=50`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getOutgoingIntegrationLogs: build.query<
      Response<IntegrationLog>,
      { query: string; id: string }
    >({
      query: ({ query, id }) => ({
        url: `/v1/psp-integrations-logs${query}&transactionIds=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useGetOutgoingListQuery,
  useLazyGetOutgoingListQuery,
  useGetOutgoingTransactionValidatorsQuery,
  useLazyGetOutgoingTransactionByIdQuery,
  useLazyGetOutgoingTransactionAmlByIdQuery,
  useUpdateOutgoingTransactionMutation,
  useCreateOutgoingTransactionMutation,
  useCheckOutgoingTransactionMutation,
  useLazyGetOutgoingTransactionWarningsQuery,
  useApplyOutgoingTransactionWarningActionMutation,
  useLazyGetOutgoingListStatusesQuery,
  useExportOutgoingTransactionMutation,
  useResetOutgoingTransactionWarningActionMutation,
  useLazyGetOutgoingTransactionWarningQuery,
  useLazyGetOutgoingTransactionWarningsInfoQuery,
  useLazyIbanValidationQuery,
  useLazyBicSearchQuery,
  useLazyBicValidationQuery,
  useLazyGetOutgoingIntegrationLogsQuery,
} = outgoingTransactionsListApi;

export const {
  endpoints: {
    getOutgoingList,
    getOutgoingTransactionById,
    getOutgoingTransactionAmlById,
    getOutgoingTransactionValidators,
    updateOutgoingTransaction,
    createOutgoingTransaction,
    checkOutgoingTransaction,
    getOutgoingTransactionWarnings,
    applyOutgoingTransactionWarningAction,
    getOutgoingListStatuses,
    exportOutgoingTransaction,
    resetOutgoingTransactionWarningAction,
    getOutgoingTransactionWarning,
    getOutgoingTransactionWarningsInfo,
    ibanValidation,
    bicSearch,
    bicValidation,
    getOutgoingIntegrationLogs,
  },
} = outgoingTransactionsListApi;
